/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useRef, useState } from 'react';

type IWindowProps = {
    url: string,
    title: string,
    width: number,
    height: number
};

type IPopupProps = IWindowProps & {
    onClose: () => void,
    onCode: (response: any, code_verifier: string) => void,
    children: React.ReactNode,
    code_verifier: string
};

const createPopup = ({ url, title, height, width }: IWindowProps) => {
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    const externalPopup = window.open(url, title, `width=${width},height=${height},left=${left},top=${top}`);
    return externalPopup;
};

const OauthPopup: React.FC<IPopupProps> = ({
    title = '',
    width = 500,
    height = 500,
    url,
    children,
    onCode,
    onClose,
    code_verifier
}: IPopupProps) => {
    const [externalWindow, setExternalWindow] = useState(Window | null);
    const intervalRef = useRef(0);

    const clearTimer = () => {
        window.clearInterval(intervalRef.current);
    };

    const onContainerClick = () => {
        setExternalWindow(
            createPopup({
                url,
                title,
                width,
                height
            })
        );
    };

    useEffect(() => {
        if (externalWindow) {
            intervalRef.current = window.setInterval(async () => {
                try {
                    const currentURL = externalWindow.location.href;
                    const params = new URL(currentURL).searchParams;
                    const code = params.get('code');
                    if (!code) {
                        return;
                    }
                    onCode(params, code_verifier);
                    clearTimer();
                    externalWindow.close();
                } catch (error) {
                    // eslint-ignore-line
                } finally {
                    if (!externalWindow || externalWindow.closed) {
                        onClose();
                        clearTimer();
                    }
                }
            }, 700);
        }
        return () => {
            if (externalWindow) externalWindow.close();
            if (onClose) onClose();
        };
    }, [externalWindow]);

    return (
        <div
            style={{ display: 'inline' }}
            onClick={() => {
                onContainerClick();
            }}
        >
            {children}
        </div>
    );
};

export default OauthPopup;
